<script>
import "@/assets/styles/kms-public.scss";
import axios from "axios";

export default {
  name: "KmsPublicDetail",

  data() {
    return {
      video: null,
      recommendations: [],
      loading: true,
      showDescription: false,
      search: "",
    };
  },

  mounted() {
    this.fetchVideo();
  },

  watch: {
    "$route.params.slug"() {
      this.fetchVideo();
    },
  },

  methods: {
    async fetchVideo() {
      this.loading = true;
      this.video = null;
      this.recommendations = [];

      try {
        const res = await axios.get(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "kms/public/videos/" +
            this.$route.params.slug
        );

        this.video = res.data.data.video;
        this.recommendations = res.data.data.recommendations || [];
      } catch (error) {
        console.error("Gagal memuat video:", error);
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      if (!this.search) return;
      this.$router.push({
        path: "/kms",
        query: { q: this.search },
      });
    },

    /* =========================
       FORMAT ANGKA (VIEW / LIKE)
    ========================== */
    formatNumber(num) {
      if (!num) return "0";
      return new Intl.NumberFormat("id-ID").format(num);
    },

    /* =========================
       FORMAT DURASI
       Support:
       - ISO 8601 (PT1H2M10S)
       - Detik (number)
    ========================== */
    formatDuration(duration) {
      if (!duration) return "-";

      // Jika format ISO 8601
      if (typeof duration === "string" && duration.startsWith("PT")) {
        const match = duration.match(
          /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/
        );

        const h = parseInt(match[1] || 0);
        const m = parseInt(match[2] || 0);
        const s = parseInt(match[3] || 0);

        if (h > 0) {
          return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(
            2,
            "0"
          )}`;
        }

        return `${m}:${String(s).padStart(2, "0")}`;
      }

      // Jika dalam detik
      if (!isNaN(duration)) {
        const total = parseInt(duration);
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;

        if (h > 0) {
          return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(
            2,
            "0"
          )}`;
        }

        return `${m}:${String(s).padStart(2, "0")}`;
      }

      return "-";
    },
  },
};
</script>

<template>
  <div class="kms-watch">
    <!-- =========================
         HEADER
    ========================== -->
    <header class="kms-header">
      <div class="kms-header-left">
        <h5>🎓 Video Pembelajaran</h5>
      </div>

      <div class="kms-header-center">
        <input
          v-model="search"
          type="text"
          class="kms-search"
          placeholder="Cari video pembelajaran..."
          @keyup.enter="handleSearch"
        />
      </div>

      <div class="kms-header-right">
        <router-link to="/login" class="kms-login-btn">
          Login
        </router-link>
      </div>
    </header>

    <!-- =========================
         LOADING
    ========================== -->
    <div v-if="loading" class="kms-loading">
      <i class="fas fa-spinner fa-spin"></i> Memuat video...
    </div>

    <!-- =========================
         CONTENT
    ========================== -->
    <div v-else-if="video" class="kms-watch-layout">
      <!-- LEFT -->
      <section class="kms-watch-main">
        <div class="kms-player">
          <iframe
            :src="video.embed_url"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>

        <h5 class="kms-watch-title">
          {{ video.title }}
        </h5>

        <!-- =========================
             META INFO
        ========================== -->
        <div class="kms-watch-meta d-flex flex-wrap gap-3">
          <span>
            📂 {{ video.category_name }}
          </span>

          <span>
            👁️ {{ formatNumber(video.view_count) }} views
          </span>

          <span>
            👍 {{ formatNumber(video.like_count) }} likes
          </span>

          <span>
            ⏱️ {{ formatDuration(video.duration) }}
          </span>
        </div>

        <!-- =========================
             DESCRIPTION
        ========================== -->
        <div class="kms-description">
          <p :class="{ collapsed: !showDescription }">
            {{ video.description }}
          </p>

          <button
            class="kms-show-more"
            @click="showDescription = !showDescription"
          >
            {{ showDescription ? "Sembunyikan" : "Tampilkan selengkapnya" }}
          </button>
        </div>
      </section>

      <!-- RIGHT -->
      <aside class="kms-watch-sidebar">
        <h6 class="mb-3">Video Terkait</h6>

        <div
          v-for="item in recommendations"
          :key="item.id"
          class="kms-recommend-card"
          @click="$router.push(`/kms/${item.slug}`)"
        >
          <img
            :src="item.thumbnail_url || item.thumbnail"
            class="kms-recommend-thumb"
          />
          <div class="kms-recommend-info">
            <p class="kms-recommend-title">
              {{ item.title }}
            </p>
            <span class="kms-recommend-meta">
              {{ item.category_name }}
            </span>
          </div>
        </div>
      </aside>
    </div>

    <!-- =========================
         EMPTY STATE
    ========================= -->
    <div v-else class="kms-loading">
      Video tidak ditemukan
    </div>
  </div>
</template>
